import {
  Button,
  Checkbox,
  Input,
  SectionTitle,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@bleu.builders/ui";
import { CheckIcon, Cross2Icon } from "@radix-ui/react-icons";
import { Check, Edit2, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useReceiptUpdate } from "./context";
import { TableActions } from "./table-actions";

export const PATCH_STATUSES = {
  CREATED: "created",
  UPDATED: "updated",
  DELETED: "deleted",
  UNCHANGED: "unchanged",
};

export function ReceiptItems() {
  const { isLoading, fieldArray } = useReceiptUpdate();

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between space-y-2">
        <div>
          <SectionTitle>Receipts Items</SectionTitle>
          <p className="text-muted-foreground">
            Here is a list of the items of the receipt
          </p>
        </div>
      </div>
      <div className="space-y-4">
        <div className="flex items-start justify-end">
          <TableActions appendItem={fieldArray.append} />
        </div>
        {isLoading ? (
          <div className="space-y-4 size-full overflow-y-auto max-h-[80vh] min-h-[10vh] animate-pulse text-muted">
            Loading...
          </div>
        ) : (
          <>
            <div className="rounded-md border dark:border-2">
              <ReceiptItemsTable items={fieldArray.fields} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ReceiptItemsTable({ items }) {
  const { setSelectedRows } = useReceiptUpdate();

  const handleToggleSelectAll = (value) => {
    if (value) {
      setSelectedRows(items.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]">
            <Checkbox
              onCheckedChange={(value) => handleToggleSelectAll(value)}
            />
          </TableHead>
          <TableHead>ID</TableHead>
          <TableHead className="min-w-[50px]">SKU</TableHead>
          <TableHead className="min-w-[50px]">Item Name</TableHead>
          <TableHead className="min-w-[50px]">Total Price</TableHead>
          <TableHead className="min-w-[150px]">Matched Product</TableHead>
          <TableHead>Eligible</TableHead>
          <TableHead>Points</TableHead>
          <TableHead>Matching Strategy</TableHead>
          <TableHead>Matching Keyword</TableHead>
          <TableHead>Confidence</TableHead>
          <TableHead>Comments</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.length ? (
          items
            .filter((item) => item.patch_status !== PATCH_STATUSES.DELETED)
            .map((row) => <ReceiptItemRow key={row.id} row={row} />)
        ) : (
          <TableRow>
            <TableCell colSpan={20}>
              <div className="text-center py-4">
                Nenhum resultado encontrado.
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

const validationSchema = z
  .object({
    name: z.string().nullable(),
    sku: z.string().nullable(),
    price: z
      .number({
        invalid_type_error: "Price must be a valid number.",
        required_error: "Price is required.",
      })
      .min(0, "Price must be greater than 0."),
    admin_comments: z.string().nullable(),
  })
  .refine((data) => data.name?.trim() || data.sku?.trim(), {
    path: ["name"],
    message: "Either name or sku is required.",
  });

function ReceiptItemRow({ row }) {
  const {
    setNonSavedUpdates,
    setEditingItems,
    editingItems,
    selectedRows,
    setSelectedRows,
    fieldArray,
    products,
    receipt,
  } = useReceiptUpdate();

  const [isEditing, setIsEditing] = useState(editingItems.includes(row.id));
  const [isSelected, setIsSelected] = useState(selectedRows.includes(row.id));
  const [selectedEligibleProduct, setSelectedEligibleProduct] = useState(null);

  const defaultValues = {
    name: row.name,
    sku: row.sku,
    price: row.price,
    eligible_product_id: row.eligible_product_id,
    admin_comments: row.admin_comments,
    points: row.points,
  };

  const form = useForm({
    defaultValues,
  });

  const handleDiscard = () => {
    form.reset();
    setNonSavedUpdates((prev) => prev.filter((id) => id !== row.id));
    setEditingItems((prev) => prev.filter((id) => id !== row.id));
  };

  const handleStartEdit = () => {
    setNonSavedUpdates((prev) => [...prev, row.id]);
    setEditingItems((prev) => [...prev, row.id]);
  };

  const handleConfirmEdit = async () => {
    const result = await validationSchema.safeParseAsync(form.getValues());

    if (!result.success) {
      toast({
        title: "Error!!!",
        description:
          "Please, fill the sku or name fields. The price is always required.",
        variant: "destructive",
      });
      return;
    }

    setNonSavedUpdates((prev) => prev.filter((id) => id !== row.id));
    setEditingItems((prev) => prev.filter((id) => id !== row.id));

    const updatedValues = form.getValues();
    const fieldIndex = fieldArray.fields.findIndex(
      (field) => field.id === row.id,
    );
    if (fieldIndex !== -1) {
      const currentField = fieldArray.fields[fieldIndex];
      fieldArray.update(fieldIndex, {
        ...currentField,
        ...updatedValues,
        patch_status:
          currentField.patch_status !== PATCH_STATUSES.CREATED
            ? PATCH_STATUSES.UPDATED
            : PATCH_STATUSES.CREATED,
      });
    }
  };

  const handleToggleSelect = (value) => {
    if (value) {
      setSelectedRows((prev) => [...prev, row.id]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleMatchChange = (value) => {
    form.setValue("eligible_product_id", value);

    if (!value) {
      form.setValue("points", 0);
      return;
    }

    const product = products.find(
      (product) => String(product.id) === String(value),
    );
    form.setValue("points", product.points_per_cents * row.price * 100);
  };

  useEffect(() => {
    setIsEditing(editingItems.includes(row.id));
    setIsSelected(selectedRows.includes(row.id));
  }, [editingItems, selectedRows]);

  useEffect(() => {
    const value = form.watch("eligible_product_id");
    const price = form.watch("price");

    if (!value) {
      return;
    }

    const product = products.find(
      (product) => product.id?.toString() === value?.toString(),
    );

    form.setValue("points", product.points_per_cents * price * 100);
  }, [form.watch("price"), form.watch("eligible_product_id")]);

  useEffect(() => {
    const eligibleProduct = products.find(
      (product) =>
        product.id?.toString() ===
        form.watch("eligible_product_id")?.toString(),
    );

    setSelectedEligibleProduct(eligibleProduct);
  }, [form.watch("eligible_product_id")]);

  const EditableCell = useMemo(
    () =>
      ({ value, name, type = "text", ...rest }) => {
        if (!isEditing) {
          return (
            <div className="min-w-[100px] max-w-[400px] whitespace-normal break-words">
              {value}
            </div>
          );
        }

        return (
          <Input
            {...form.register(name, { valueAsNumber: type === "number" })}
            className="w-[100px] max-w-[400px] h-8"
            type={type}
            {...rest}
          />
        );
      },
    [isEditing, form],
  );

  return (
    <TableRow className={isEditing ? "bg-muted/50" : ""}>
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(value) => handleToggleSelect(value)}
        />
      </TableCell>
      <TableCell>
        {row.id.toString().startsWith("newid") ? "-" : row.id}
      </TableCell>
      <TableCell className="min-w-[50px]">
        <EditableCell value={row.sku || "N/A"} name="sku" />
      </TableCell>
      <TableCell className="min-w-[50px]">
        <EditableCell value={row.name} name="name" />
      </TableCell>
      <TableCell className="min-w-[50px]">
        <div className="flex">
          {isEditing ? null : <span>$</span>}
          <EditableCell
            value={form.watch("price")}
            name="price"
            type="number"
            step=".01"
            defaultValue={form.watch("price")}
          />
        </div>
      </TableCell>
      <TableCell className="min-w-[150px]">
        {isEditing ? (
          <Select.SelectRoot
            onValueChange={(value) => handleMatchChange(value)}
            defaultValue={row.eligible_product_id?.toString()}
          >
            <Select.SelectTrigger className="w-full min-w-[150px]">
              <Select.SelectValue placeholder="Select a matched product" />
            </Select.SelectTrigger>
            <Select.SelectContent>
              <Select.SelectItem key="none" value={null}>
                None
              </Select.SelectItem>
              {products.map((product) => (
                <Select.SelectItem
                  key={product.id}
                  value={product.id.toString()}
                >
                  {`${product.product_name} - ${product.points_per_cents} points per cent of dollar (${product?.product_active ? "Active" : "Inactive"})`}
                </Select.SelectItem>
              ))}
            </Select.SelectContent>
          </Select.SelectRoot>
        ) : selectedEligibleProduct ? (
          `${selectedEligibleProduct.product_name} - ${selectedEligibleProduct.points_per_cents} points per cent of dollar (${selectedEligibleProduct?.product_active ? "Active" : "Inactive"})`
        ) : null}
      </TableCell>

      <TableCell>
        {form.watch("eligible_product_id") || receipt?.source === "external" ? (
          <CheckIcon className="size-4 bg-primary text-primary-foreground rounded-full" />
        ) : (
          <Cross2Icon className="size-4 text-muted-foreground" />
        )}
      </TableCell>
      <TableCell>
        {form.watch("points") !== null
          ? Math.round(form.watch("points"))
          : "N/A"}
      </TableCell>

      <TableCell>{row.matched_strategy}</TableCell>
      <TableCell>{row.matched_keyword}</TableCell>
      <TableCell>
        {row.match_confidence != null
          ? Number(row.match_confidence).toFixed(2)
          : "-"}
      </TableCell>

      <TableCell>
        <EditableCell value={row.admin_comments} name="admin_comments" />
      </TableCell>

      <TableCell className="flex items-center gap-2">
        {isEditing ? (
          <>
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleConfirmEdit()}
              >
                <Check className="size-4" />
              </Button>
              {!(row.sku === "" && row.name === "") && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDiscard()}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
          </>
        ) : (
          <Button variant="ghost" size="icon" onClick={() => handleStartEdit()}>
            <Edit2 className="size-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
